import Box from '@amzn/awsui-components-core/box';
import { ColDef, ColGroupDef, DefaultMenuItem, GetContextMenuItemsParams, MenuItemDef, SideBarDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import React, { useEffect, useMemo } from 'react';
import { useTheme } from 'src/components/context/ThemeContextProvider';
import { useAgGridExpandedState } from '../hooks/useAgGridExpandedState';
import { usePnlGridViewport } from 'src/components/star-grid/hooks/usePnlGridViewport';
import { PnlGridRow, PnlPivotModel } from '../starGridTypes';
import {
  buildAutoGroupColumnDef,
  buildColumnDefsFromModel,
  buildGetDataPath,
  pnlDefaultColDef,
  pnlGetRowClass,
  pnlSumAggFunc
} from '../utils/pnlGrid';
import './styles/pnl-report-grid.scss';

export interface PnlReportGridProps {
  readonly model: PnlPivotModel;
  /** True while a (re-)query is in flight — shows the grid's loading overlay. */
  readonly loading: boolean;
  /** Selected display-scale id (drives client-side value formatting via grid context). */
  readonly scaleId: string;
  /** Dimensions currently in the row hierarchy — used to filter the drill menu. */
  readonly groupBy: readonly string[];
  /** Dimensions the report can drill into (not already grouped). */
  readonly drillableDimensions: readonly string[];
  /** Called when the user picks a drill dimension from the context menu. */
  readonly onDrillDown: (dimension: string) => void;
  /** Optional field → human label map for the row-hierarchy header + drill menu. */
  readonly dimensionLabels?: Record<string, string>;
}

// Static — hoisted so a new object identity isn't created per render.
const GRID_SIDEBAR: SideBarDef = {
  toolPanels: [{ id: 'columns', labelDefault: 'Columns', labelKey: 'columns', iconKey: 'columns', toolPanel: 'agColumnsToolPanel' }]
};

const PNL_STATUS_BAR = {
  statusPanels: [{ statusPanel: 'agAggregationComponent' }]
};

/**
 * The PnL data grid: dimensions form a Prophecy-style tree row hierarchy (pinned
 * auto-group column), scenario × month value columns are grouped by scenario, and
 * variance columns are computed client-side. Ported from the xPT PnlReportGrid, adapted
 * to FinSuite's AG Grid v35 Theming API + Cloudscape.
 */
const PnlReportGrid: React.FC<PnlReportGridProps> = ({ model, loading, scaleId, groupBy, drillableDimensions, onDrillDown, dimensionLabels }) => {
  const { agGridTheme } = useTheme();
  const { gridRef, captureExpandedState, restoreExpandedState } = useAgGridExpandedState();
  const viewportRef = usePnlGridViewport(model.rows.length > 0);

  // pnlSum treats an all-null group as null (em dash) rather than 0.
  const aggFuncs = useMemo(() => ({ pnlSum: pnlSumAggFunc }), []);

  // Re-render cells when the display scale changes (values are formatted client-side).
  useEffect(() => {
    gridRef.current?.api?.refreshCells({ force: true });
  }, [scaleId, gridRef]);

  const columnDefs = useMemo<(ColDef<PnlGridRow> | ColGroupDef)[]>(
    () => buildColumnDefsFromModel(model.columnGroups, model.calculatedColumns),
    [model.columnGroups, model.calculatedColumns]
  );

  const autoGroupColumnDef = useMemo<ColDef<PnlGridRow>>(
    () => buildAutoGroupColumnDef(model.dimensionFields, dimensionLabels),
    [model.dimensionFields, dimensionLabels]
  );

  const getDataPath = useMemo(() => buildGetDataPath(model.dimensionFields), [model.dimensionFields]);

  // Drill-down context menu — capture expansion state BEFORE re-query so it can be
  // restored after new rows arrive (see restoreExpandedState).
  const getContextMenuItems = (params: GetContextMenuItemsParams<PnlGridRow>): (DefaultMenuItem | MenuItemDef<PnlGridRow>)[] => {
    const defaults: (DefaultMenuItem | MenuItemDef<PnlGridRow>)[] = ['copy', 'copyWithHeaders', 'separator', 'export'];
    const drillable = drillableDimensions.filter((d) => !groupBy.includes(d));
    if (params.node?.footer || drillable.length === 0) return defaults;
    return [
      {
        name: 'Drill down by',
        subMenu: drillable.map((dim) => ({
          name: dimensionLabels?.[dim] ?? dim,
          action: () => {
            captureExpandedState();
            onDrillDown(dim);
          }
        }))
      },
      'separator',
      ...defaults
    ];
  };

  if (model.rows.length === 0) {
    return (
      <Box padding="l" textAlign="center" color="text-status-inactive">
        No rows match the current filters.
      </Box>
    );
  }

  return (
    <div className="sg-pnl-grid-wrapper" ref={viewportRef}>
      <div className="sg-pnl-grid">
        <AgGridReact<PnlGridRow>
          ref={gridRef}
          rowData={model.rows}
          columnDefs={columnDefs}
          defaultColDef={pnlDefaultColDef}
          loading={loading}
          treeData
          getDataPath={getDataPath}
          autoGroupColumnDef={autoGroupColumnDef}
          groupDefaultExpanded={0}
          aggFuncs={aggFuncs}
          getRowClass={pnlGetRowClass}
          suppressAggFuncInHeader
          context={{ scaleId }}
          theme={agGridTheme}
          sideBar={GRID_SIDEBAR}
          statusBar={PNL_STATUS_BAR}
          cellSelection
          columnHoverHighlight
          rowBuffer={20}
          getContextMenuItems={getContextMenuItems}
          onRowDataUpdated={restoreExpandedState}
        />
      </div>
    </div>
  );
};

export default PnlReportGrid;
